import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Mail, Clock } from "lucide-react";
import "../ThankYou/ThankYou.css";

const RedirectModal = ({ onConfirm, onCancel }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.45)",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        padding: "36px 32px 28px",
        maxWidth: 400,
        width: "90%",
        textAlign: "center",
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: "50%",
          background: "rgba(0,212,189,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 16px",
        }}
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#00D4BD" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </div>
      <h3 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 18, color: "#1F2937", marginBottom: 10 }}>
        Redirect to Homepage?
      </h3>
      <p style={{ fontFamily: "Inter, sans-serif", fontSize: 14, color: "#6B7280", lineHeight: "22px", marginBottom: 24 }}>
        This page is only valid after a demo request. You will be redirected to the homepage.
      </p>
      <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
        <button
          onClick={onCancel}
          style={{
            flex: 1,
            padding: "10px 0",
            borderRadius: 8,
            border: "1.5px solid #E5E7EB",
            background: "#fff",
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            fontSize: 14,
            color: "#374151",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          style={{
            flex: 1,
            padding: "10px 0",
            borderRadius: 8,
            border: "none",
            background: "#00D4BD",
            fontFamily: "Inter, sans-serif",
            fontWeight: 700,
            fontSize: 14,
            color: "#000",
            cursor: "pointer",
          }}
        >
          Yes, Go Home
        </button>
      </div>
    </div>
  </div>
);

const ThanksDemoRequest = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { email } = location.state || {};

  const emailRef = useRef(email || null);
  const [showModal, setShowModal] = useState(!emailRef.current);

  useEffect(() => {
    if (emailRef.current) {
      // Clear state from history so back-navigation won't restore it
      navigate(location.pathname, { replace: true, state: null });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const emailDisplay = emailRef.current;

  return (
    <>
      <Helmet>
        <title>Thank You | TeamGrid</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {showModal && (
        <RedirectModal
          onConfirm={() => navigate("/ai-workforce-intelligence", { replace: true })}
          onCancel={() => setShowModal(false)}
        />
      )}

      <div className="thank-you-outer">
        <section className="thank-you-section">
          <div className="thank-you-head-block">
            <div className="thank-you-overlay">
              <img src="/thanks_tick.webp" alt="Request received" className="thank-you-tick-img" />
            </div>
            <h1 className="thank-you-heading">Thank You!</h1>
            <p className="thank-you-subtitle">
              We&apos;ve received your demo request and will be in touch shortly.
            </p>
          </div>

          <div className="thank-you-details-card">
            <h3 className="thank-you-details-title">What we&apos;ll do next</h3>
            <div className="thank-you-details-list">
              <div className="thank-you-details-row">
                <div className="thank-you-details-icon-wrap">
                  <Mail className="thank-you-details-icon" size={20} stroke="#00D1C1" />
                </div>
                <div className="thank-you-details-text">
                  <span className="thank-you-details-label">We&apos;ll contact you at</span>
                  <span className="thank-you-details-value">{emailDisplay}</span>
                </div>
              </div>
              <div className="thank-you-details-row">
                <div className="thank-you-details-icon-wrap">
                  <Clock className="thank-you-details-icon" size={20} stroke="#00D1C1" />
                </div>
                <div className="thank-you-details-text">
                  <span className="thank-you-details-label">Response time</span>
                  <span className="thank-you-details-value">Within 1–2 business days</span>
                </div>
              </div>
            </div>
          </div>

          <div className="thank-you-next-block">
            <h3 className="thank-you-next-title">What happens next?</h3>
            <div className="thank-you-next-steps">
              <div className="thank-you-step-card">
                <div className="thank-you-step-num">1</div>
                <div className="thank-you-step-content">
                  <span className="thank-you-step-heading">We review your request</span>
                  <span className="thank-you-step-desc">Our team will get in touch to schedule your demo</span>
                </div>
              </div>
              <div className="thank-you-step-card">
                <div className="thank-you-step-num">2</div>
                <div className="thank-you-step-content">
                  <span className="thank-you-step-heading">Check your email</span>
                  <span className="thank-you-step-desc">We&apos;ll send you a reply with next steps</span>
                </div>
              </div>
              <div className="thank-you-step-card">
                <div className="thank-you-step-num">3</div>
                <div className="thank-you-step-content">
                  <span className="thank-you-step-heading">Join your demo</span>
                  <span className="thank-you-step-desc">See TeamGrid in action with a personalized walkthrough</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer className="thank-you-footer" style={{ borderTopColor: "rgba(0, 0, 0, 0.2)" }}>
          <div className="thank-you-footer-inner">
            <span className="thank-you-footer-text">Questions? Email us at</span>
            <a href="mailto:hello@teamgrid.ai" className="thank-you-footer-link">
              hello@teamgrid.ai
            </a>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ThanksDemoRequest;
